import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import SectionIntro from "./SectionIntro";

// Images are matched to each tile's shape:
// [0] big landscape, [1] tall portrait, [2][3] landscape, [4][5] wide panoramic
const images = [
  "/images/gallery/exterior5.jpg",
  "/images/gallery/atlas2.jpg",
  "/images/gallery/living3.jpg",
  "/images/gallery/room3.jpg",
  "/images/gallery/hero22.jpg",
  "/images/gallery/ext.jpg",
];

// Asymmetric mosaic spans (applied at md+; mobile stays a simple 2-col grid)
const layout = [
  "md:col-span-2 md:row-span-2",
  "md:col-span-1 md:row-span-2",
  "",
  "",
  "md:col-span-2",
  "md:col-span-2",
];

const TeaserGallery = () => {
  const { t } = useTranslation();

  return (
    <section id="teaser-gallery" className="py-24 md:py-32 bg-cream-dark/40 overflow-hidden">
      <div className="max-w-7xl mx-auto px-5 md:px-10">
        <SectionIntro
          eyebrow={t("labels.gallery")}
          title={t("gallery.title")}
          lead={t("gallery.subtitle")}
          className="mb-16 md:mb-20"
        />

        <motion.div
          className="grid grid-cols-2 md:grid-cols-4 auto-rows-[150px] sm:auto-rows-[190px] md:auto-rows-[230px] gap-3 md:gap-4"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-50px" }}
          variants={{
            hidden: { opacity: 0 },
            visible: { opacity: 1, transition: { staggerChildren: 0.1 } },
          }}
        >
          {images.map((src, index) => (
            <motion.div
              key={index}
              className={`group relative overflow-hidden bg-cream-dark ${layout[index]}`}
              variants={{
                hidden: { opacity: 0, y: 30 },
                visible: {
                  opacity: 1,
                  y: 0,
                  transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1] },
                },
              }}
            >
              <img
                src={src}
                alt={t("gallery.imageAlt", { number: index + 1 })}
                className="w-full h-full object-cover transition-transform duration-[1.2s] ease-out group-hover:scale-110"
                loading="lazy"
                decoding="async"
              />
              <div className="absolute inset-0 bg-ink/0 group-hover:bg-ink/25 transition-colors duration-500" />
              <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                <span className="w-12 h-12 border border-cream/70 rounded-full flex items-center justify-center text-cream">
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={1.5}
                      d="M12 5v14m-7-7h14"
                    />
                  </svg>
                </span>
              </div>
            </motion.div>
          ))}
        </motion.div>

        <motion.div
          className="mt-14 md:mt-16 text-center"
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
        >
          <Link
            to="/gallery"
            className="inline-flex items-center justify-center border border-ink/30 text-ink hover:bg-ink hover:text-cream text-[12px] uppercase tracking-[0.22em] py-4 px-10 transition-all duration-300"
          >
            {t("gallery.exploreButton")}
          </Link>
        </motion.div>
      </div>
    </section>
  );
};

export default TeaserGallery;
